import { useMemo } from 'react';

import { useCategoryTreeItem } from '../categoryTree';
import { KuxFilteredCategoriesDTO, UIDAPresentationTypeEnum, tvpil } from '../compat/tvpil';
import type { RhinoImageRatio } from '../image';

import { MoreLink } from './types';
import { ImageSize, useImageSettings } from './useImageSettings';

export type UseRailSettingsProps = {
  defaultImageRatio?: RhinoImageRatio;
  defaultLandscapeImageHeight?: number;
  defaultPortraitImageHeight?: number;
  imageSize?: ImageSize;
  referenceId: string;
};

export type RailSettings = {
  branding: KuxFilteredCategoriesDTO['uiDesignElements'];
  id: number;
  imageHeight?: number;
  imageType?: number;
  imageWidth?: number;
  moreLink: MoreLink | undefined;
  parentalPinProtected: boolean;
  shouldShowEntitlementIndicator: boolean;
  shouldShowRating: boolean;
  title: string;
};

export function useRailSettings({
  referenceId,
  imageSize,
  defaultPortraitImageHeight,
  defaultLandscapeImageHeight,
  defaultImageRatio,
}: UseRailSettingsProps): RailSettings | undefined {
  const category = useCategoryTreeItem(referenceId);

  const imageSettings = useImageSettings({
    referenceId,
    imageSize,
    defaultPortraitImageHeight,
    defaultLandscapeImageHeight,
    defaultImageRatio,
  });

  const settings = useMemo<RailSettings | undefined>(() => {
    if (!category) {
      return undefined;
    }

    const shouldShowMoreLink = Boolean(category.railProperty.presentShowMore);
    const moreLink = shouldShowMoreLink
      ? createMoreLink({ referenceId, link: category.link, subtype: category.subtype })
      : undefined;

    return {
      branding: category.uiDesignElements,
      id: category.id,
      ...imageSettings,
      moreLink,
      parentalPinProtected: Boolean(category.parentalPinProtected),
      shouldShowEntitlementIndicator: Boolean(category.asideProperty?.presentEntitlementIndicator),
      shouldShowRating: Boolean(category.asideProperty?.presentRating),
      title: category.name,
    };
  }, [category, imageSettings, referenceId]);

  return settings;
}

function createMoreLink({
  referenceId,
  subtype,
  link,
}: {
  referenceId: string;
  subtype: UIDAPresentationTypeEnum;
  link: KuxFilteredCategoriesDTO['link'];
}) {
  if (!link) {
    return;
  }

  const { inAppLink, targetGridCategoryId, typeOfAppLink } = link;

  const targetLink = targetGridCategoryId ?? inAppLink;

  if (!targetLink || !typeOfAppLink) {
    return;
  }

  const isTargetingGrid = targetGridCategoryId && typeOfAppLink === 'grid';

  if (isTargetingGrid && !tvpil.categoryTreeItemsMap.has(targetGridCategoryId)) {
    return;
  }

  return {
    inAppLink: targetLink,
    link: tvpil.generateMenuLink(
      typeOfAppLink,
      targetLink,
      referenceId,
      subtype && tvpil.isNewSubtypeSupported(subtype) ? subtype : undefined,
    ),
    linkType: typeOfAppLink,
    referenceId,
  };
}
